/**
 * Pure helpers for open-advance entries (ENG-27316): the ageing card, the KPI
 * card sums, and the labels and state filters the entries section renders.
 *
 * No React, no network — everything is a function of its arguments, with
 * `today` passed in so tests pin the clock.
 */
import type { WalletEntry, WalletFacilityTotals } from "../types/wallet";
import { WALLET_ENTRY_SOURCE, WALLET_ENTRY_STATE } from "../types/wallet";

const MS_PER_DAY = 24 * 60 * 60 * 1000;
/** IST is UTC+5:30 with no daylight saving, so a fixed offset is exact. */
const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;

/** The IST calendar day an instant falls on, as a day count since the epoch. */
function istDay(ms: number): number {
  return Math.floor((ms + IST_OFFSET_MS) / MS_PER_DAY);
}

/**
 * Calendar days, in IST, from today to the day `expiresAt` falls on — never
 * negative. Counting dates rather than flooring the time left means a 30-day
 * hold reads 30 on the day it starts (not 29 for a whole day), 1 the day before
 * it expires and 0 on the day itself, whatever the time of day. One already
 * past its day has 0. An unparseable stamp also reads as 0, so a malformed
 * entry lands in "nearing expiry" rather than looking safely far off.
 */
export function daysUntil(expiresAt: string | null | undefined, today: Date): number {
  const end = expiresAt ? new Date(expiresAt).getTime() : NaN;
  if (Number.isNaN(end)) return 0;
  return Math.max(0, istDay(end) - istDay(today.getTime()));
}

export interface ExpiryBucket {
  label: string;
  amount: number;
  count: number;
}

/** True for an entry that still holds spendable credit. */
function isOpenWithBalance(entry: WalletEntry | null | undefined): entry is WalletEntry {
  return !!entry && entry.state === WALLET_ENTRY_STATE.OPEN && entry.balance > 0;
}

/**
 * Buckets entries by how long their credit has left, for the ageing card.
 *
 * Keyed on `expires_at`, never `created_at` plus a hold period: the stamp is
 * set at creation and does not move when a claim is released, and reading it
 * keeps the hold length out of the UI entirely.
 *
 * - `edges` are inclusive upper bounds in days left, so the default
 *   [10, 20, 30] gives "Expires in 0–10 / 11–20 / 21–30 days", nearest first.
 *   An OPEN entry with more than the last edge left falls in the last bucket.
 * - EXPIRED entries go to the final "Expired" bucket regardless of their stamp.
 * - Entries with no balance left, and CONSUMED / REFUNDED entries, are
 *   excluded — they hold no outstanding credit.
 */
export function bucketByExpiry(
  entries: WalletEntry[] | undefined | null,
  today: Date,
  edges: number[] = [10, 20, 30],
): ExpiryBucket[] {
  const buckets: ExpiryBucket[] = edges.map((edge, i) => ({
    label: i === 0 ? `Expires in 0–${edge} days` : `Expires in ${edges[i - 1] + 1}–${edge} days`,
    amount: 0,
    count: 0,
  }));
  const expired: ExpiryBucket = { label: "Expired", amount: 0, count: 0 };

  for (const entry of entries ?? []) {
    if (!entry || entry.balance <= 0) continue;

    if (entry.state === WALLET_ENTRY_STATE.EXPIRED) {
      expired.amount += entry.balance;
      expired.count += 1;
      continue;
    }
    if (entry.state !== WALLET_ENTRY_STATE.OPEN) continue;

    const left = daysUntil(entry.expires_at, today);
    const index = edges.findIndex((edge) => left <= edge);
    const target = index === -1 ? buckets[buckets.length - 1] : buckets[index];
    target.amount += entry.balance;
    target.count += 1;
  }

  return [...buckets, expired];
}

/** Open advance card: credit still held, i.e. the balance of OPEN entries. */
export function openAdvanceTotal(entries: WalletEntry[] | undefined | null): number {
  return (entries ?? []).filter(isOpenWithBalance).reduce((sum, e) => sum + e.balance, 0);
}

/**
 * Nearing expiry card: the balance of OPEN entries in the nearest-expiry
 * bucket — by definition the same money the ageing card's first bar shows.
 */
export function nearingExpiryTotal(
  entries: WalletEntry[] | undefined | null,
  today: Date,
  withinDays = 10,
): number {
  return (entries ?? [])
    .filter(isOpenWithBalance)
    .filter((e) => daysUntil(e.expires_at, today) <= withinDays)
    .reduce((sum, e) => sum + e.balance, 0);
}

/**
 * Where an entry came from (ENG-28531): "Credit note CN-7 · PO X",
 * "Revised PO X" or "Cancelled PO X" — whichever parts exist. Never the
 * internal credit-note id: the number is what a vendor recognises.
 *
 * A credit-note number always names the credit note. Otherwise the source type
 * decides, and an unknown one shows the PO alone: only a credit-note entry may
 * say "Credit note".
 */
export function entrySourceLabel(
  entry: Pick<WalletEntry, "source_type" | "credit_note_number" | "source_po_code">,
): string {
  const code = entry.source_po_code ?? "";
  const join = (...parts: string[]) => parts.filter(Boolean).join(" ");
  if (!entry.credit_note_number) {
    if (entry.source_type === WALLET_ENTRY_SOURCE.PO_REVISED_NO_CN) return join("Revised PO", code);
    if (entry.source_type === WALLET_ENTRY_SOURCE.PO_CANCELLED) return join("Cancelled PO", code);
  }
  const note = entry.credit_note_number
    ? `Credit note ${entry.credit_note_number}`
    : entry.source_type === WALLET_ENTRY_SOURCE.CREDIT_NOTE_NO_UTR
      ? "Credit note"
      : "";
  const po = code ? `PO ${code}` : "";
  return [note, po].filter(Boolean).join(" · ") || "Open advance";
}

/** The entries section's state filters, in the order they render. */
export const ENTRY_GROUPS = [
  { state: WALLET_ENTRY_STATE.OPEN, label: "Open" },
  { state: WALLET_ENTRY_STATE.EXPIRED, label: "Expired" },
  { state: WALLET_ENTRY_STATE.REFUNDED, label: "Refunded" },
  { state: WALLET_ENTRY_STATE.CONSUMED, label: "Used up" },
] as const;

/** One per-facility figure, added up across facilities and rounded to the paisa. */
function sumFacilities(
  facilities: WalletFacilityTotals[],
  key: "netted_this_month" | "advance_unadjusted",
): number {
  const total = facilities.reduce((sum, f) => sum + (f[key] ?? 0), 0);
  return Math.round(total * 100) / 100;
}

/**
 * Open advance set off against invoices this IST month, across facilities
 * (ENG-27892). VIP computes each facility's figure; this only adds them up.
 */
export function nettedThisMonthTotal(facilities: WalletFacilityTotals[]): number {
  return sumFacilities(facilities, "netted_this_month");
}

/**
 * PO advance not yet adjusted against invoices, across facilities (ENG-27744).
 * VIP's `advance_unadjusted` is credited minus adjusted; a revoked advance
 * already reads 0.
 */
export function advanceHeldTotal(facilities: WalletFacilityTotals[]): number {
  return sumFacilities(facilities, "advance_unadjusted");
}
